import React, { useState, useEffect } from 'react';
import './PlayerHeader.css';

// Get the API base URL (same as api.js)
const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_BASE_URL) {
        return process.env.REACT_APP_API_BASE_URL;
    }
    if (process.env.NODE_ENV === 'development') {
        return 'http://localhost:8000';
    }
    return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

const PlayerHeader = ({ player }) => {
    const [playerInfo, setPlayerInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (player?.id) {
            fetchPlayerInfo();
        }
    }, [player?.id]);

    const fetchPlayerInfo = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await fetch(`${API_BASE_URL}/api/v1/dev/player-logs/player/${player.id}/info`);
            if (!response.ok) {
                // Check if we're getting HTML instead of JSON (common in production)
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('text/html')) {
                    throw new Error('API server not available. Please check if the backend is running.');
                }
                throw new Error('Failed to fetch player info');
            }
            
            const data = await response.json();
            setPlayerInfo(data);
        } catch (err) {
            // Handle JSON parsing errors specifically
            if (err.name === 'SyntaxError' && err.message.includes('Unexpected token')) {
                setError('API server not available. Please check if the backend is running.');
            } else {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        } catch {
            return dateString;
        }
    };

    const formatHeight = (height) => {
        if (!height) return 'N/A';
        return height;
    };

    const formatWeight = (weight) => {
        if (!weight) return 'N/A';
        return `${weight} lbs`;
    };

    const safeRender = (value) => {
        if (!value) return 'N/A';
        if (typeof value === 'string') return value;
        if (typeof value === 'object') {
            if (Object.keys(value).length === 0) return 'N/A';
            return Object.values(value).join(', ');
        }
        return String(value);
    };

    if (loading) {
        return (
            <div className="player-header-container">
                <div className="player-header-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading player information...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="player-header-container">
                <div className="player-header-error">
                    <p>Could not load player information</p>
                </div>
            </div>
        );
    }

    return (
        <div className="player-header-container">
            <div className="player-header-hero">
                {/* Hero Background with Player Image */}
                <div className="hero-background">
                    {playerInfo?.cutout && (
                        <img 
                            src={playerInfo.cutout} 
                            alt={playerInfo.name}
                            className="hero-player-image"
                        />
                    )}
                    <div className="hero-overlay"></div>
                </div>

                {/* Main Content */}
                <div className="hero-content">
                    <div className="player-main-info">
                        {/* Large Player Image */}
                        <div className="player-image-container">
                            {playerInfo?.cutout ? (
                                <img 
                                    src={playerInfo.cutout} 
                                    alt={playerInfo.name}
                                    className="player-main-image"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                            ) : null}
                            {(!playerInfo?.cutout || playerInfo.cutout === '') && (
                                <div className="player-image-placeholder">
                                    <span>{playerInfo?.name?.charAt(0) || '?'}</span>
                                </div>
                            )}
                        </div>

                        {/* Player Name and Basic Info */}
                        <div className="player-basic-details">
                            <h1 className="player-name">{playerInfo?.name || player.name}</h1>
                            
                            <div className="player-meta">
                                <div className="meta-item">
                                    <span className="meta-label">Team</span>
                                    <span className="meta-value">{safeRender(playerInfo?.team)}</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Position</span>
                                    <span className="meta-value">{safeRender(playerInfo?.position)}</span>
                                </div>
                                {playerInfo?.nationality && (
                                    <div className="meta-item">
                                        <span className="meta-label">Nationality</span>
                                        <span className="meta-value">{playerInfo.nationality}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Player Stats Grid */}
                    <div className="player-stats-grid">
                        <div className="stat-card">
                            <div className="stat-icon">📏</div>
                            <div className="stat-content">
                                <span className="stat-label">Height</span>
                                <span className="stat-value">{formatHeight(playerInfo?.height)}</span>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">⚖️</div>
                            <div className="stat-content">
                                <span className="stat-label">Weight</span>
                                <span className="stat-value">{formatWeight(playerInfo?.weight)}</span>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">🎂</div>
                            <div className="stat-content">
                                <span className="stat-label">Birth Date</span>
                                <span className="stat-value">{formatDate(playerInfo?.birth_date)}</span>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">🎓</div>
                            <div className="stat-content">
                                <span className="stat-label">College</span>
                                <span className="stat-value">{safeRender(playerInfo?.college)}</span>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">🏀</div>
                            <div className="stat-content">
                                <span className="stat-label">Draft Year</span>
                                <span className="stat-value">{safeRender(playerInfo?.draft_year)}</span>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">📋</div>
                            <div className="stat-content">
                                <span className="stat-label">Draft Pick</span>
                                <span className="stat-value">
                                    {playerInfo?.draft_round && playerInfo?.draft_number 
                                        ? `Round ${playerInfo.draft_round}, Pick ${playerInfo.draft_number}`
                                        : 'N/A'
                                    }
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlayerHeader; 